import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PROFESSIONS } from '../data/siteData';
import { useFormModal } from '../context/FormModalContext';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5002');

const VERIFY_START_TIMEOUT_MS = 15000;
const CAPTCHA_IMAGES_TIMEOUT_MS = 15000;
const VERIFY_START_TIMEOUT_ERROR = 'Сервер слишком долго отвечает. Нажмите ещё раз.';
const CAPTCHA_LOAD_ERROR = 'Капча не загрузилась. Нажмите ещё раз.';

const fetchWithTimeout = async (url, options = {}, timeoutMs = VERIFY_START_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timeoutId);
    }
};

const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
});

const waitForCaptchaImages = (challengeUrl, options = [], timeoutMs = CAPTCHA_IMAGES_TIMEOUT_MS) => {
    const urls = [challengeUrl, ...options.map((option) => option.url)].filter(Boolean);

    if (urls.length === 0) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error('captcha images timeout'));
        }, timeoutMs);

        Promise.all(urls.map(loadImage))
            .then((result) => {
                clearTimeout(timeoutId);
                resolve(result);
            })
            .catch((error) => {
                clearTimeout(timeoutId);
                reject(error);
            });
    });
};

const isAbortError = (error) =>
    error?.name === 'AbortError' || /abort|timeout/i.test(String(error?.message || ''));

const reportVerifyClientIssue = (payload = {}) => {
    try {
        fetch(`${API_URL}/api/verify/client-issue`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true
        }).catch(() => { });
    } catch (_) {
        // ignore reporting failures
    }
};

const validatePhone = (phone) => {
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');

    if (!cleanedPhone) {
        return { valid: false, error: 'Телефон обязателен для заполнения' };
    }

    const phoneRegex = /^\+?[0-9]+$/;
    if (!phoneRegex.test(cleanedPhone)) {
        return { valid: false, error: 'Телефон может содержать только цифры и знак + в начале' };
    }

    const digitsOnly = cleanedPhone.replace(/^\+/, '');
    const startsWith7or8 = digitsOnly.startsWith('7') || digitsOnly.startsWith('8');

    if (startsWith7or8) {
        if (digitsOnly.length < 11) {
            return { valid: false, error: 'Телефон должен содержать минимум 11 цифр (включая 7 или 8)' };
        }
        if (digitsOnly.length > 15) {
            return { valid: false, error: 'Телефон не должен превышать 15 цифр' };
        }
    } else {
        if (digitsOnly.length < 10) {
            return { valid: false, error: 'Телефон должен содержать минимум 10 цифр' };
        }
        if (digitsOnly.length > 15) {
            return { valid: false, error: 'Телефон не должен превышать 15 цифр' };
        }
    }

    return { valid: true, error: '' };
};

const ApplicationFormModal = () => {
    const { showForm, setShowForm } = useFormModal();
    const scrollPositionRef = useRef(0);

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [verificationId, setVerificationId] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState('idle');
    const [verificationMode, setVerificationMode] = useState('call');
    const [verificationNumber, setVerificationNumber] = useState('');
    const [verificationNumberTel, setVerificationNumberTel] = useState('');
    const [captchaType, setCaptchaType] = useState('arithmetic');
    const [captchaSessionId, setCaptchaSessionId] = useState(null);
    const [captchaQuestion, setCaptchaQuestion] = useState('');
    const [captchaAnswer, setCaptchaAnswer] = useState('');
    const [captchaChallengeUrl, setCaptchaChallengeUrl] = useState('');
    const [captchaOptions, setCaptchaOptions] = useState([]);
    const [captchaAttempts, setCaptchaAttempts] = useState(0);
    const [verifyStartLoading, setVerifyStartLoading] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyError, setVerifyError] = useState('');
    const [verifyLocked, setVerifyLocked] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '+7',
        city: '',
        profession: 'Сопровождение грузов'
    });

    const buildCaptchaAssetUrl = (assetPath) => `${API_URL}${assetPath}`;

    const resetCaptchaState = () => {
        setCaptchaSessionId(null);
        setCaptchaQuestion('');
        setCaptchaAnswer('');
        setCaptchaChallengeUrl('');
        setCaptchaOptions([]);
        setCaptchaAttempts(0);
    };

    const applyCaptchaResponse = (data) => {
        setCaptchaSessionId(data.sessionId || null);

        if (data.captchaType === 'image') {
            setCaptchaType('image');
            setCaptchaChallengeUrl(data.challengeUrl ? buildCaptchaAssetUrl(data.challengeUrl) : '');
            setCaptchaOptions((data.options || []).map((option) => ({
                ...option,
                url: buildCaptchaAssetUrl(option.url)
            })));
            setCaptchaQuestion('');
            setCaptchaAnswer('');
            setCaptchaAttempts(0);
            return;
        }

        setCaptchaType('arithmetic');
        setCaptchaQuestion(data.question || '');
        setCaptchaAnswer('');
        setCaptchaChallengeUrl('');
        setCaptchaOptions([]);
        setCaptchaAttempts(0);
    };

    const resetVerificationLock = () => {
        setVerifyLocked(false);
        setVerificationStatus('idle');
        setVerifyError('');
        resetCaptchaState();
    };

    const handleRetryAfterBlock = async () => {
        try {
            const response = await fetch(`${API_URL}/api/verify/ip-status`);
            const data = await response.json();

            if (data.blocked) {
                const minutes = data.remainingMinutes || 1;
                setVerifyError(`Слишком много попыток. Попробуйте снова через ${minutes} мин.`);
                return;
            }

            resetVerificationLock();
        } catch (error) {
            console.error('IP status check error:', error);
            setVerifyError('Не удалось проверить статус блокировки. Проверьте соединение и попробуйте снова.');
        }
    };

    useEffect(() => {
        if (showForm) {
            fetch(`${API_URL}/api/verify/mode`)
                .then((res) => res.json())
                .then((data) => {
                    if (data.mode) {
                        setVerificationMode(data.mode);
                    }
                    if (data.captchaType) {
                        setCaptchaType(data.captchaType);
                    }
                    if (data.verificationNumber) {
                        setVerificationNumber(data.verificationNumber);
                    }
                    if (data.verificationNumberTel) {
                        setVerificationNumberTel(data.verificationNumberTel);
                    }
                })
                .catch((err) => {
                    console.error('Error fetching verification mode:', err);
                });
        }
    }, [showForm]);

    useEffect(() => {
        if (!showForm || verificationMode !== 'call' || verificationStatus !== 'pending' || !verificationId) {
            return undefined;
        }

        let cancelled = false;

        const pollStatus = async () => {
            try {
                const response = await fetch(`${API_URL}/api/verify/status/${verificationId}`);
                const data = await response.json();
                if (cancelled || !response.ok) return;

                if (data.verificationNumberDisplay || data.verificationNumber) {
                    setVerificationNumber(data.verificationNumberDisplay || data.verificationNumber);
                    setVerificationNumberTel(data.verificationNumberTel || data.verificationNumber || '');
                }

                if (data.status === 'verified') {
                    setVerificationStatus('verified');
                    setVerifyError('');
                }
            } catch (error) {
                console.error('Verify status poll error:', error);
            }
        };

        pollStatus();
        const timer = setInterval(pollStatus, 2500);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [showForm, verificationMode, verificationStatus, verificationId]);

    useEffect(() => {
        if (showForm) {
            scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPositionRef.current}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            const savedScrollPosition = scrollPositionRef.current;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            setTimeout(() => {
                window.scrollTo(0, savedScrollPosition);
            }, 0);
        }

        return () => {
            if (!showForm) {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
            }
        };
    }, [showForm]);

    const resetForm = () => {
        setShowForm(false);
        setPhoneError('');
        setError('');
        setVerifyError('');
        setVerificationId(null);
        setVerificationNumber('');
        setVerificationNumberTel('');
        setVerificationStatus('idle');
        resetCaptchaState();
        setVerifyLocked(false);
        setFormSubmitted(false);
        setConsentAccepted(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!consentAccepted) {
            setError('Необходимо согласие на обработку персональных данных');
            setLoading(false);
            return;
        }

        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.valid) {
            setPhoneError(phoneValidation.error);
            setLoading(false);
            return;
        }

        if (verificationStatus !== 'verified') {
            const errorMessage =
                verificationMode === 'captcha'
                    ? 'Подтвердите заявку (решите капчу) перед отправкой'
                    : 'Подтвердите номер телефона перед отправкой заявки';
            setError(errorMessage);
            setLoading(false);
            return;
        }

        if (verificationMode === 'call' && !verificationId) {
            setError('Подтвердите номер телефона перед отправкой заявки');
            setLoading(false);
            return;
        }

        if (verificationMode === 'captcha' && !captchaSessionId) {
            setError('Подтвердите заявку (решите капчу) перед отправкой');
            setLoading(false);
            return;
        }

        try {
            const submitData = {
                ...formData,
                verificationId: verificationMode === 'call' ? verificationId : null,
                captchaSessionId: verificationMode === 'captcha' ? captchaSessionId : null
            };

            const response = await fetch(`${API_URL}/api/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submitData)
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Ошибка при отправке формы');
                setLoading(false);
                return;
            }

            setFormSubmitted(true);
            setLoading(false);
            setVerifyError('');
            setVerifyLocked(false);
            setVerificationStatus('idle');
            setVerificationId(null);
            setVerificationNumber('');
            setVerificationNumberTel('');
            resetCaptchaState();
        } catch (submitError) {
            console.error('Submit error:', submitError);
            setError('Ошибка соединения. Проверьте интернет.');
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            let phoneValue = value;

            if (phoneValue.length === 0) {
                phoneValue = '+7';
            } else if (!phoneValue.startsWith('+7')) {
                if (phoneValue.startsWith('+')) {
                    const afterPlus = phoneValue.slice(1);
                    if (afterPlus.startsWith('7')) {
                        phoneValue = '+7' + afterPlus.slice(1);
                    } else {
                        phoneValue = '+7' + afterPlus.replace(/^7?/, '');
                    }
                } else if (phoneValue.startsWith('7')) {
                    phoneValue = '+7' + phoneValue.slice(1);
                } else {
                    phoneValue = '+7' + phoneValue.replace(/^\+?7?/, '');
                }
            }

            setFormData((prev) => ({ ...prev, [name]: phoneValue }));
            setVerificationId(null);
            setVerificationStatus('idle');
            setVerificationNumber('');
            setVerificationNumberTel('');
            resetCaptchaState();
            setVerifyError('');
            setVerifyLocked(false);

            const validation = validatePhone(phoneValue);
            setPhoneError(validation.error);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleStartVerify = async () => {
        setVerifyError('');
        setVerifyLocked(false);

        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.valid) {
            setPhoneError(phoneValidation.error);
            return;
        }

        setVerifyStartLoading(true);
        const startedAt = Date.now();

        try {
            const response = await fetchWithTimeout(
                `${API_URL}/api/verify/start`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phone: formData.phone })
                },
                VERIFY_START_TIMEOUT_MS
            );

            const data = await response.json();

            if (!response.ok) {
                const errText = data.error || 'Не удалось запустить подтверждение';
                setVerifyError(errText);
                if (response.status === 429 || data.blocked) {
                    setVerifyLocked(true);
                    setVerificationStatus('locked');
                }
                return;
            }

            if (data.mode === 'captcha') {
                setVerificationMode('captcha');
                setVerifyLocked(false);

                if (data.captchaType === 'image') {
                    const challengeUrl = data.challengeUrl ? `${API_URL}${data.challengeUrl}` : '';
                    const options = (data.options || []).map((option) => ({
                        ...option,
                        url: `${API_URL}${option.url}`
                    }));

                    try {
                        await waitForCaptchaImages(challengeUrl, options, CAPTCHA_IMAGES_TIMEOUT_MS);
                    } catch (imageLoadError) {
                        console.error('Captcha images load error:', imageLoadError);
                        reportVerifyClientIssue({
                            kind: 'images_timeout',
                            message: imageLoadError?.message || 'images failed',
                            sessionId: data.sessionId || null,
                            durationMs: Date.now() - startedAt,
                            details: {
                                challengeUrl: data.challengeUrl || null,
                                optionsCount: (data.options || []).length
                            }
                        });
                        resetCaptchaState();
                        setVerificationStatus('idle');
                        setVerifyError(CAPTCHA_LOAD_ERROR);
                        return;
                    }

                    applyCaptchaResponse(data);
                    setVerificationStatus('pending');
                } else {
                    applyCaptchaResponse(data);
                    setVerificationStatus('pending');
                }
            } else {
                if (data.mode) {
                    setVerificationMode(data.mode);
                }
                setVerificationId(data.verificationId || null);
                setVerificationNumber(data.verificationNumberDisplay || data.verificationNumber || '');
                setVerificationNumberTel(data.verificationNumberTel || data.verificationNumber || '');
                setVerificationStatus('pending');
            }
        } catch (startError) {
            console.error('Start verify error:', startError);
            reportVerifyClientIssue({
                kind: isAbortError(startError) ? 'start_timeout' : 'start_network',
                message: startError?.message || 'start failed',
                durationMs: Date.now() - startedAt
            });
            setVerifyError(
                isAbortError(startError)
                    ? VERIFY_START_TIMEOUT_ERROR
                    : 'Ошибка соединения. Проверьте интернет.'
            );
            resetCaptchaState();
            setVerificationStatus('idle');
        } finally {
            setVerifyStartLoading(false);
        }
    };

    const handleImageCaptchaSelect = async (selectedIndex) => {
        if (!captchaSessionId || verifyLoading || verifyLocked) {
            return;
        }

        setVerifyError('');
        setVerifyLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/verify/check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId: captchaSessionId, selectedIndex })
            });

            const data = await response.json();

            if (!response.ok) {
                const errText = data.error || 'Ошибка проверки капчи';
                setVerifyError(errText);
                if (data.blocked || response.status === 429 || errText.toLowerCase().includes('10 минут')) {
                    setVerifyLocked(true);
                    setVerificationStatus('locked');
                }
                setVerifyLoading(false);
                return;
            }

            setVerificationStatus('verified');
            setVerifyLoading(false);
        } catch (imageCaptchaError) {
            console.error('Verify image captcha error:', imageCaptchaError);
            setVerifyError('Ошибка соединения. Проверьте интернет.');
            setVerifyLoading(false);
        }
    };

    const handleVerifyPin = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setVerifyError('');
        setVerifyLocked(false);

        if (verificationMode === 'captcha' && captchaType === 'arithmetic') {
            if (!captchaSessionId) {
                setVerifyError('Не удалось найти сессию капчи. Запустите проверку заново.');
                return;
            }

            const answerNum = parseInt(captchaAnswer.trim(), 10);
            if (isNaN(answerNum) || captchaAnswer.trim() === '') {
                setVerifyError('Введите ответ на капчу');
                return;
            }

            setVerifyLoading(true);

            try {
                const response = await fetch(`${API_URL}/api/verify/check`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId: captchaSessionId, answer: answerNum })
                });

                const data = await response.json();

                if (!response.ok) {
                    const errText = data.error || 'Ошибка проверки капчи';
                    setVerifyError(errText);
                    if (data.attemptsLeft !== undefined) {
                        setCaptchaAttempts(3 - data.attemptsLeft);
                    }
                    if (data.blocked || errText.toLowerCase().includes('превышено количество попыток')) {
                        setVerifyLocked(true);
                        setVerificationStatus('locked');
                    }
                    setVerifyLoading(false);
                    return;
                }

                setVerificationStatus('verified');
                setVerifyLoading(false);
            } catch (captchaError) {
                console.error('Verify captcha error:', captchaError);
                setVerifyError('Ошибка соединения. Проверьте интернет.');
                setVerifyLoading(false);
            }
            return;
        }

        if (verificationMode === 'captcha') {
            return;
        }
    };

    const isPhoneValid = validatePhone(formData.phone).valid && !phoneError;

    const handlePhoneKeyDown = (e) => {
        const input = e.target;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart < 2 || selectionEnd < 2) {
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                setTimeout(() => input.setSelectionRange(2, 2), 0);
            } else if (e.key.length === 1 && selectionStart < 2 && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                const currentValue = input.value;
                const newValue = '+7' + e.key + currentValue.slice(2);
                setFormData((prev) => ({ ...prev, phone: newValue }));
                setTimeout(() => input.setSelectionRange(3, 3), 0);
            }
        }
    };

    const handlePhonePaste = (e) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const input = e.target;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;
        const currentValue = input.value;

        let newValue;
        if (selectionStart < 2) {
            newValue = '+7' + pastedText + currentValue.slice(Math.max(2, selectionEnd));
        } else {
            newValue = currentValue.slice(0, selectionStart) + pastedText + currentValue.slice(selectionEnd);
        }

        if (!newValue.startsWith('+7')) {
            if (newValue.startsWith('+')) {
                const afterPlus = newValue.slice(1);
                newValue = afterPlus.startsWith('7')
                    ? '+7' + afterPlus.slice(1)
                    : '+7' + afterPlus.replace(/^7?/, '');
            } else if (newValue.startsWith('7')) {
                newValue = '+7' + newValue.slice(1);
            } else {
                newValue = '+7' + newValue.replace(/^\+?7?/, '');
            }
        }

        setFormData((prev) => ({ ...prev, phone: newValue }));
        setPhoneError(validatePhone(newValue).error);

        setTimeout(() => {
            const newPosition = Math.min(selectionStart + pastedText.length, newValue.length);
            input.setSelectionRange(newPosition, newPosition);
        }, 0);
    };

    const handlePhoneBlur = (e) => {
        let phoneValue = e.target.value;
        if (!phoneValue.startsWith('+7')) {
            if (phoneValue === '' || phoneValue === '+') {
                phoneValue = '+7';
            } else if (phoneValue.startsWith('7')) {
                phoneValue = '+' + phoneValue;
            } else if (phoneValue.startsWith('+')) {
                phoneValue = '+7' + phoneValue.slice(1);
            } else {
                phoneValue = '+7' + phoneValue;
            }
            setFormData((prev) => ({ ...prev, phone: phoneValue }));
            setPhoneError(validatePhone(phoneValue).error);
        }
    };

    if (!showForm) {
        return null;
    }

    return (
        <div className="modal-overlay" onClick={() => !formSubmitted && resetForm()}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                {verifyLocked && (
                    <div className="lock-overlay">
                        <div className="lock-modal">
                            <h3>Подтверждение не пройдено</h3>
                            <p>
                                {verifyError || (captchaType === 'image'
                                    ? 'Неверный выбор картинки. Повторная попытка будет доступна через 10 минут.'
                                    : 'Превышено количество попыток ввода кода. Попробуйте позже или используйте другой номер.')}
                            </p>
                            <button
                                type="button"
                                className="submit-button"
                                style={{ marginTop: '16px' }}
                                onClick={handleRetryAfterBlock}
                            >
                                Попробовать снова
                            </button>
                        </div>
                    </div>
                )}
                {!formSubmitted ? (
                    <>
                        <button type="button" className="modal-close" onClick={resetForm}>
                            ×
                        </button>
                        <h2 className="modal-title">Оставить заявку</h2>
                        <p className="modal-subtitle">Введите данные ниже, мы вам перезвоним</p>

                        <form onSubmit={handleSubmit} className="application-form">
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Ваше имя"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Ваш телефон"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    onKeyDown={handlePhoneKeyDown}
                                    onPaste={handlePhonePaste}
                                    onBlur={handlePhoneBlur}
                                    className={`form-input ${phoneError ? 'form-input-error' : ''}`}
                                    required
                                />
                                {phoneError && <div className="form-field-error">{phoneError}</div>}
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Ваш город"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    required
                                />
                            </div>

                            {verificationStatus === 'verified' && (
                                <div className="form-note" style={{ color: '#2e7d32', fontWeight: 600 }}>
                                    {verificationMode === 'captcha'
                                        ? 'Капча решена верно. Можно отправить заявку.'
                                        : 'Телефон подтверждён. Можно отправить заявку.'}
                                </div>
                            )}

                            {verificationStatus !== 'verified' && verificationStatus !== 'pending' && (
                                <div className="form-group">
                                    <button
                                        type="button"
                                        className="submit-button"
                                        onClick={handleStartVerify}
                                        disabled={
                                            !isPhoneValid ||
                                            verifyStartLoading ||
                                            verificationStatus === 'pending' ||
                                            verificationStatus === 'locked'
                                        }
                                    >
                                        {verifyStartLoading
                                            ? 'Запускаем проверку...'
                                            : verificationMode === 'captcha'
                                                ? 'Подтвердить заявку'
                                                : 'Подтвердить номер'}
                                    </button>
                                    {verifyError && <div className="form-error">{verifyError}</div>}
                                    <p className="form-note">
                                        {verificationMode === 'captcha'
                                            ? 'Подтвердите заявку (решите капчу), чтобы отправить заявку.'
                                            : 'Подтвердите номер, чтобы отправить заявку. Мы покажем номер, на который нужно позвонить.'}
                                    </p>
                                </div>
                            )}

                            {verificationStatus === 'pending' && verificationMode === 'captcha' && captchaType === 'image' && (
                                <div className="application-form image-captcha-form">
                                    <div className="captcha-label">Если вы человек, нажмите на похожую картинку</div>
                                    {captchaChallengeUrl && (
                                        <div className="captcha-challenge-wrap">
                                            <img
                                                src={captchaChallengeUrl}
                                                alt="Эталонное изображение"
                                                className="captcha-challenge-image"
                                            />
                                        </div>
                                    )}
                                    <div className="captcha-options-grid">
                                        {captchaOptions.map((option, index) => (
                                            <button
                                                key={`${option.id}-${index}`}
                                                type="button"
                                                className="captcha-option-button"
                                                onClick={() => handleImageCaptchaSelect(index)}
                                                disabled={verifyLoading || verifyLocked}
                                            >
                                                <img
                                                    src={option.url}
                                                    alt={`Вариант ${index + 1}`}
                                                    className="captcha-option-image"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                    {verifyError && <div className="form-error">{verifyError}</div>}
                                </div>
                            )}

                            {verificationStatus === 'pending' && verificationMode === 'captcha' && captchaType === 'arithmetic' && (
                                <div className="application-form" style={{ marginTop: '10px' }}>
                                    <div className="form-group">
                                        <div className="captcha-label">Решите пример</div>
                                        <div className="captcha-question">{captchaQuestion} = ?</div>
                                        {captchaAttempts > 0 && captchaAttempts < 3 && !verifyLocked && (
                                            <div className="captcha-attempts-warning">
                                                Осталось попыток: {3 - captchaAttempts}
                                            </div>
                                        )}
                                        <input
                                            type="number"
                                            name="captcha"
                                            placeholder="Введите ответ"
                                            value={captchaAnswer}
                                            onChange={(e) => setCaptchaAnswer(e.target.value)}
                                            className="form-input"
                                            inputMode="numeric"
                                            required
                                            disabled={verifyLocked}
                                        />
                                    </div>
                                    {verifyError && <div className="form-error">{verifyError}</div>}
                                    <button
                                        type="button"
                                        className="submit-button"
                                        disabled={verifyLoading || verifyLocked}
                                        onClick={handleVerifyPin}
                                    >
                                        {verifyLoading ? 'Проверяем...' : 'Подтвердить ответ'}
                                    </button>
                                </div>
                            )}

                            {verificationStatus === 'pending' && verificationMode === 'call' && (
                                <div className="verify-call-box">
                                    <div className="captcha-label">Позвоните на номер</div>
                                    {verificationNumberTel ? (
                                        <a href={`tel:${verificationNumberTel}`} className="verify-call-number">
                                            {verificationNumber || verificationNumberTel}
                                        </a>
                                    ) : (
                                        <div className="verify-call-number">{verificationNumber || 'номер загружается...'}</div>
                                    )}
                                    <p className="verify-call-wait">
                                        Звоните именно с того телефона, который указали в заявке.
                                        После звонка статус подтвердится автоматически.
                                    </p>
                                    {verifyError && <div className="form-error">{verifyError}</div>}
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="profession">Выберите один из вариантов</label>
                                <div className="select-wrapper">
                                    <select
                                        id="profession"
                                        name="profession"
                                        value={formData.profession}
                                        onChange={handleInputChange}
                                        className="form-select"
                                        required
                                    >
                                        {PROFESSIONS.map(({ title }) => (
                                            <option key={title} value={title}>
                                                {title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <label className="form-consent">
                                <input
                                    type="checkbox"
                                    checked={consentAccepted}
                                    onChange={(e) => setConsentAccepted(e.target.checked)}
                                />
                                <span>
                                    Согласен с{' '}
                                    <Link to="/privacy" onClick={() => setShowForm(false)}>
                                        политикой конфиденциальности
                                    </Link>{' '}
                                    и{' '}
                                    <Link to="/terms" onClick={() => setShowForm(false)}>
                                        пользовательским соглашением
                                    </Link>
                                </span>
                            </label>

                            {error && <div className="form-error">{error}</div>}

                            <button
                                type="submit"
                                className="submit-button"
                                disabled={
                                    loading ||
                                    !consentAccepted ||
                                    verificationStatus !== 'verified' ||
                                    !formData.name?.trim() ||
                                    !formData.city?.trim() ||
                                    !formData.profession
                                }
                            >
                                {loading ? 'Отправка...' : 'Отправить заявку'}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="form-success">
                        <div className="success-icon">✓</div>
                        <h2 className="success-title">Заявка оформлена</h2>
                        <p className="success-text">Спасибо! Мы свяжемся с вами в ближайшее время.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicationFormModal;
